<template>
  <div class="card">
    <SelectedItemHeader
      :cardHeader="rider.searchString"
      @clear="$emit('clearRider')"
    />

    <ItemActions
      :itemTriggers="riderTriggers"
      :allTriggers="allRiderTriggers"
      @select-trigger="selectTrigger"
      :selectedTrigger="selectedTrigger"
    />

    <!-- using a dynamic component here, allows us to pass in a different component if more data is needed to be sent -->
    <component
      @form-submission="submitTrigger"
      @tracker-form-submission="submitTrackerTrigger"
      v-if="selectedTrigger"
      :is="componentMap[selectedTrigger.formComponent].component"
      v-bind="componentMap[selectedTrigger.formComponent].props"
      :loading="formSubmitting"
      :formTitle="selectedTrigger.verboseName"
      :item="rider"
      itemType="Rider"
      displayFieldName="fullName"
    />

    <div class="card-body">
      <div class="row">
        <StateBadge
          class="col col-auto mb-1"
          :states="riderStates"
          :stateKey="rider.state"
        />
        <LinkedItem
          v-if="rider.current_tracker"
          class="col col-auto"
          itemType="tracker"
          :itemObject="rider.current_tracker"
        />
        <div v-else class="col col-auto text-muted small my-auto">
          No tracker
        </div>
        <LinkedItem
          v-if="rider.pair"
          class="col col-auto"
          itemType="rider"
          :itemObject="rider.pair"
        />
        <div v-if="rider.pre_assigned_tracker" class="col col-auto row">
          <LinkedItem
            class="col col-auto"
            itemType="tracker"
            :itemObject="preassignedTracker"
          />
          <TagBadge
            class="col col-auto"
            badgeText="Pre-assigned"
            color="info"
          />
        </div>
      </div>
      <div class="row mt-2">
        <SocialMediaLinks
          class="col"
          :social-media="rider.social_media"
          :stripe-id="rider.stripe_id"
        />
      </div>
    </div>

    <div v-if="rider.active_scratch" class="card-body pt-0">
      <b-alert show variant="warning" class="mb-0">
        <div class="d-flex align-items-center">
          <i class="fas fa-ban mr-2" />
          <strong>Scratched</strong>
          <span class="ml-auto small text-muted">{{ formattedScratchTime }}</span>
        </div>
        <div class="mt-2 small">
          <div>
            <span class="text-muted">Type:</span>
            {{ rider.active_scratch.scratch_type.display_name }}
          </div>
          <div>
            <span class="text-muted">Location:</span>
            {{ rider.active_scratch.location }}
          </div>
          <div>
            <span class="text-muted">By:</span>
            {{ activeScratchBy }}
          </div>
          <div v-if="rider.active_scratch.notes">
            <span class="text-muted">Notes:</span>
            {{ rider.active_scratch.notes }}
          </div>
        </div>
      </b-alert>
    </div>

    <div v-if="rider.active_disqualification" class="card-body pt-0">
      <b-alert show variant="danger" class="mb-0">
        <div class="d-flex align-items-center">
          <i class="fas fa-gavel mr-2" />
          <strong>Disqualified</strong>
          <span class="ml-auto small text-muted">{{ formattedDisqualificationTime }}</span>
        </div>
        <div class="mt-2">
          <span class="text-muted small">Reason:</span>
          {{ rider.active_disqualification.reason }}
        </div>
      </b-alert>
    </div>

    <RiderDetail
      :rider="rider"
      :riderInfo="riderInfo"
      :baseURL="baseURL"
      :raceShortName="raceShortName"
    />


    <ItemTimeLine
      :sortedEvents="rider.history"
      noEventsMsg="No events for this rider"
      :allItemTriggers="allRiderTriggers"
      timelineTitle="History"
      itemType="rider"
    />

    <ItemTimeLine
      :sortedEvents="rider.transactions"
      noEventsMsg="No transactions for this rider"
      timelineTitle="Deposit account"
      itemType="rider"
    />

    <ItemTimeLine
      :sortedEvents="rider.tracker_history"
      noEventsMsg="No trackers assinged to this rider"
      timelineTitle="Trackers"
      itemType="rider"
    />
  </div>
</template>

<script>
import RiderDetail from "@/components/renderingComponents/RiderDetail.vue";
import ItemActions from "@/components/renderingComponents/ItemActions";
import TriggerWithVolunteer from "@/components/forms/TriggerWithVolunteer";
import AddRemoveMoney from "@/components/forms/AddRemoveMoney";
import ItemTimeLine from "@/components/renderingComponents/ItemTimeline";
import SelectedItemHeader from "@/components/renderingComponents/SelectedItemHeader";
import LinkedItem from "@/components/renderingComponents/LinkedItem";
import StateBadge from "@/components/renderingComponents/StateBadge.vue";
import TagBadge from "@/components/renderingComponents/TagBadge.vue";
import GiveTracker from "@/components/forms/GiveTracker";
import RetrieveTracker from "@/components/forms/RetrieveTracker";
import RequiresFurtherAttention from "@/components/forms/RequiresFurtherAttention";
import ManualTrackerDeposit from "@/components/forms/ManualTrackerDeposit";
import Scratch from "@/components/forms/Scratch";
import Disqualify from "@/components/forms/Disqualify";
import SocialMediaLinks from "@/components/renderingComponents/SocialMediaLinks.vue";

import moment from "moment-timezone";
import { retrieveItemByUUID } from "@/common-functions.js";

export default {
  name: "SelectedRider",
  components: {
    RiderDetail,
    RequiresFurtherAttention,
    ItemActions,
    TriggerWithVolunteer,
    ItemTimeLine,
    SelectedItemHeader,
    LinkedItem,
    StateBadge,
    TagBadge,
    GiveTracker,
    RetrieveTracker,
    ManualTrackerDeposit,
    Scratch,
    SocialMediaLinks,
  },
  props: {
    baseURL: String,
    rider: {
      type: Object,
    },
    allRiderTriggers: {
      type: Object,
    },
    riderStates: Object,
    riderInfo: Object,
    trackers: {
      type: Object,
    },
  },
  data() {
    return {
      selectedTrigger: null,
      formSubmitting: false,
      transactionTimelineFields: ["amount_in_pence"],
    };
  },
  computed: {
    raceShortName() {
      return this.$store.state.racesStore.selectedRace.short_name;
    },
    formattedDisqualificationTime() {
      if (!this.rider.active_disqualification) return "";
      return moment(this.rider.active_disqualification.datetime).format(
        "DD MMM YYYY HH:mm"
      );
    },
    preassignedTracker() {
      if (this.rider.pre_assigned_tracker) {
        return retrieveItemByUUID(
          this.$store,
          "tracker",
          this.rider.pre_assigned_tracker
        );
      }
      return null;
    },
    componentMap() {
      return {
        "trigger-with-volunteer": {
          component: TriggerWithVolunteer,
        },
        "requires-further-attention": {
          component: RequiresFurtherAttention,
        },
        "add-remove-money": {
          component: AddRemoveMoney,
        },
        "give-tracker": {
          component: GiveTracker,
          props: {
            trackers: Object.values(this.trackers).filter(
              (tracker) => tracker.state == "available"
            ),
            preassignedTracker: this.preassignedTracker,
          },
        },
        "retrieve-tracker": {
          component: RetrieveTracker,
        },
        "manual-tracker-deposit": {
          component: ManualTrackerDeposit,
        },
        "scratch": {
          component: Scratch,
        },
        "disqualify": {
          component: Disqualify,
        },
      };
    },
    formattedScratchTime() {
      if (!this.rider.active_scratch) return "";
      return moment(this.rider.active_scratch.scratched_at).format(
        "DD MMM YYYY HH:mm"
      );
    },
    activeScratchBy() {
      const scratch = this.rider.active_scratch;
      if (!scratch) return "";
      if (scratch.user) {
        return `${scratch.user.first_name} ${scratch.user.last_name}`.trim();
      }
      return "system";
    },
    riderTriggers() {
      let triggers = this.rider.triggers;
      if (!this.rider.current_tracker) {
        triggers.push("give_tracker");
      } else {
        triggers.push("retrieve_tracker");
      }
      return triggers;
    },
  },
  watch: {
    rider() {
      this.selectedTrigger = null;
    },
  },
  methods: {
    selectTrigger(trigger) {
      // close trigger form if selecting same button again
      if (
        this.selectedTrigger &&
        this.selectedTrigger.trigger == trigger.trigger
      ) {
        this.selectedTrigger = null;
      } else {
        this.selectedTrigger = trigger;
      }
    },
    submitTrigger(formData) {
      this.formSubmitting = true;
      this.$store
        .dispatch("ridersStore/submitRiderTrigger", {
          riderUUID: this.rider.uuid,
          trigger: this.selectedTrigger.trigger,
          data: formData,
        })
        .then(() => {
          this.$emit("refresh-rider", this.rider.uuid);
          this.selectedTrigger = null;
          this.formSubmitting = false;
        })
        .catch(() => {
          this.selectedTrigger = null;
          this.formSubmitting = false;
        });
    },
    submitTrackerTrigger(formData) {
      this.formSubmitting = true;
      this.$store
        .dispatch("trackersStore/submitTrackerTrigger", {
          trackerUUID: formData.tracker,
          trigger: this.selectedTrigger.trigger,
          data: formData.data,
        })
        .then(() => {
          this.$emit("refresh-rider", this.rider.uuid);
          this.selectedTrigger = null;
          this.formSubmitting = false;
        })
        .catch(() => {
          this.selectedTrigger = null;
          this.formSubmitting = false;
        });
    },
  },
};
</script>

<style scoped>
.card {
  text-align: left;
}
</style>
